/**
 * Human sign-off of the built-in effect shaders (W3-04; DESIGN.md D24, R13; WORKPACKAGES Appendix C H5).
 *
 * The ten built-ins are reimplemented from their behaviour, so every one is badged "approximate" until a person has
 * compared it with Phira captures on the `#/dev/effects` comparer (which writes a sign-off record as JSON) and the
 * orchestrator copies that record here. A built-in listed here is 'verified': the editor drops its "approximate"
 * badge. The table starts empty; nothing else changes the fidelity.
 */

import type { BuiltinShader } from '../../../../core/model/types';

/** Evidence that a person compared a built-in with Phira and found it matching (copied from the comparer's record). */
export interface BuiltinSignOff {
  /** ISO date of the comparison (YYYY-MM-DD). */
  readonly date: string;
  /** Who compared it. */
  readonly tester: string;
  /** Phira version and platform of the captures, e.g. "0.8.2, Android 14". */
  readonly phira: string;
  /** Repository path (or link) of the stored sign-off record and captures. */
  readonly evidence: string;
}

/** Signed-off built-ins (Appendix C H5). Empty until a person signs one off. */
export const SIGNED_OFF: Readonly<Partial<Record<BuiltinShader, BuiltinSignOff>>> = {};

/** The sign-off of a built-in, or null while it is approximate. */
export function signOffOf(
  name: BuiltinShader,
  table: Readonly<Partial<Record<BuiltinShader, BuiltinSignOff>>> = SIGNED_OFF,
): BuiltinSignOff | null {
  return table[name] ?? null;
}
